/**
 * UserService.js
 * Manages User Persistence and Projects.
 * Implements the "Next-Gen Data Schema" (Hybrid Multi-Tenant).
 */
import {
    doc,
    getDoc,
    setDoc,
    updateDoc,
    addDoc,
    collection,
    serverTimestamp
} from 'firebase/firestore';
import { db } from './firebase';
import { UserSchema, UserProjectSchema } from '@emoji-photo/shared/schemas';

export const UserService = {

    /**
     * ensureUser
     * Checks if user profile exists. If not, creates it.
     * Checks for pending viral transfers (Stub).
     * @param {Object} user - Firebase Auth User Object
     * @param {String} currentTenantId - Context for 'home_tenant' logic
     */
    async ensureUser(user, currentTenantId = 'emoji-photo') {
        if (!user || !user.uid) return null;

        const userRef = doc(db, 'users', user.uid);

        try {
            const userSnap = await getDoc(userRef);

            if (!userSnap.exists()) {
                console.log(`[UserService] Creating new user profile for ${user.uid}`);

                // Create Base Profile
                const newProfile = {
                    uid: user.uid,
                    email: user.email,
                    displayName: user.displayName || 'Anonymous',
                    photoURL: user.photoURL || '',
                    home_tenant: currentTenantId,
                    tenants: {
                        [currentTenantId]: {
                            role: 'member', // Default role
                            joinedAt: new Date() // Will be serverTimestamp()
                        }
                    },
                    billing: { // Valid Subscription Schema
                        plan_id: 'free',
                        plan_name: 'Free',
                        credits_remaining: 50,
                        credits_total: 50,
                        usage_lifetime: 0,
                        monthly_image_quota: 5,
                        rate_limit_req_per_min: 5,
                        is_active: true,
                        payment_method: 'none',
                        currency: 'USD',
                        purchase_date: new Date(),
                        renewal_date: new Date()
                    },
                    global_preferences: {
                        theme: 'auto', // Fixed enum
                        notifications: true,
                        language: 'en'
                    },
                    created_at: new Date(),
                    last_login: new Date(),
                    version: 2
                };

                // Validate (Log on fail, but allow for now if needed, or throw)
                // Validate (Log on fail, but allow for now to prevent funnel blockers)
                try {
                    UserSchema.parse(newProfile);
                } catch (e) {
                    console.error('[UserService] Schema Validation Failed (Soft Fail):', e);
                    // SRE Rule: Do not crash the user journey for analytics/validation errors in prod
                }

                // Convert Dates to serverTimestamp for DB
                const dbPayload = {
                    ...newProfile,
                    created_at: serverTimestamp(),
                    last_login: serverTimestamp(),
                    tenants: {
                        [currentTenantId]: {
                            role: 'member',
                            joinedAt: serverTimestamp()
                        }
                    }
                };

                await setDoc(userRef, dbPayload);

                // STUB: Check Pending Transfers here
                // this.checkPendingTransfers(user.email);

                return newProfile;
            } else {
                // Update last_login
                await updateDoc(userRef, {
                    last_login: serverTimestamp()
                });
                return userSnap.data();
            }
        } catch (err) {
            console.error('[UserService] Error ensuring user:', err);
            throw err;
        }
    },

    /**
     * saveProject
     * Saves a collage project to users/{uid}/projects
     * @param {String} uid 
     * @param {Object} projectData 
     */
    async saveProject(uid, projectData) {
        if (!uid) throw new Error("User ID required to save project");

        try {
            const projectsRef = collection(db, 'users', uid, 'projects');

            const payload = {
                projectId: crypto.randomUUID(), // Generate ID for schema validation (addDoc makes its own but we validate payload)
                name: projectData.name || 'Untitled Collage',
                tenantId: projectData.tenantId || 'emoji-photo',
                assets: projectData.assets || [], // Image refs
                is_favorite: false,
                created_at: new Date(),
                updated_at: new Date()
            };

            // Schema Validation
            try {
                UserProjectSchema.parse(payload);
            } catch (e) {
                console.error('[UserService] Invalid Project Data:', e);
                throw e;
            }

            // Prepare for DB
            const dbPayload = {
                ...payload,
                // Remove projectId if we want Firestore to auto-gen, OR use setDoc with payload.projectId
                // Here addDoc generates new ID. 
                // If we want consistency, we should use setDoc(doc(projectsRef, payload.projectId), ...)
                created_at: serverTimestamp(),
                updated_at: serverTimestamp()
            };
            // remove projectId from payload if addDoc? 
            // The schema expects projectId. 
            // Let's rely on addDoc ID and just validate the REST of the fields.
            delete dbPayload.projectId;


            const docRef = await addDoc(projectsRef, dbPayload);
            console.log(`[UserService] Project saved: ${docRef.id}`);
            return docRef.id;
        } catch (err) {
            console.error('[UserService] Failed to save project:', err);
            throw err;
        }
    },

    /**
     * getProjects
     * Lists user's projects
     */
    /* async getProjects(uid) { ... } */
};
